"use client";

import { useEffect, useState } from "react";
import { ArrowRight, Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-24 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative inline-flex h-9 items-center px-4 text-sm font-medium text-foreground bg-transparent hover:bg-transparent focus:bg-transparent">
                <span className="relative font-default">
                  {item.label}
                  <span className="absolute -bottom-1 left-0 h-[2px] w-0 bg-primary transition-all duration-300 ease-out group-hover:w-full" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 px-6">
        <div className="h-10 w-3/4 bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-2/3 bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-3/4 bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-1/2 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-2 px-6">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} onClick={onNavigate} data-index={i} className="text-3xl font-semibold tracking-tight text-foreground py-2 border-b border-border hover:text-primary transition-colors font-default">
          {item.label}
        </Link>)}
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="403" id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 bg-background text-foreground ${scrolled ? "bg-background/70 backdrop-blur-lg border-b border-border" : "bg-background border-b border-transparent"}`}>
      <div className={`container mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between transition-all duration-300 ${scrolled ? "h-16" : "h-20"}`}>
        <div className="flex items-center shrink-0">
          <Link to="/" aria-label="Hope Torchlighters home" className="flex items-center">
            <WvcLogo className="h-7 w-auto" />
          </Link>
        </div>

        <div className="hidden lg:flex flex-1 justify-center">
          <MenuProvider menu_id="header_menu">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <Link to="#" className="hidden md:inline-flex text-sm font-medium text-foreground hover:text-primary transition-colors px-3 py-2 font-default">
            Get Support
          </Link>

          <Button asChild className="hidden sm:inline-flex rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 px-5 font-semibold shadow-sm font-default">
            <Link to="#">
              Donate Now
              <ArrowRight className="ml-1 size-4" />
            </Link>
          </Button>

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden rounded-lg text-foreground hover:bg-accent" aria-label="Open menu">
                <Menu className="size-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-md bg-background p-0 flex flex-col">
              <SheetHeader className="px-6 pt-6 pb-2">
                <SheetTitle className="text-left">
                  <Link to="/" onClick={() => setMobileOpen(false)} aria-label="Hope Torchlighters home">
                    <WvcLogo className="h-7 w-auto" />
                  </Link>
                </SheetTitle>
              </SheetHeader>

              <div className="flex-1 overflow-y-auto pt-6">
                <MenuProvider menu_id="header_menu">
                  <MobileMenuConsumer onNavigate={() => setMobileOpen(false)} />
                </MenuProvider>
              </div>

              <div className="px-6 pb-8 pt-4 border-t border-border flex flex-col gap-3">
                <Link to="#" onClick={() => setMobileOpen(false)} className="text-base font-medium text-foreground hover:text-primary transition-colors font-default">
                  Get Support
                </Link>
                <Button asChild className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-semibold font-default">
                  <Link to="#" onClick={() => setMobileOpen(false)}>
                    Donate Now
                    <ArrowRight className="ml-1 size-4" />
                  </Link>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </section>;
}